import React, { useState, useEffect, useImperativeHandle } from "react";
import { useLoader } from "../../components/loader/loader";
import { getData, deleteData } from "../../services/Axios/Axios";
import { ApiUrl } from "../../config/apiConfig";
import { PaginationControls } from "../../models/pagination";
import NoRecord from "../../components/NoDataScreen/NoRecord";
import Pagination from "../../components/pagination/pagination";
import messageService from "../../services/message/messageService";
import { FileText, Pencil, Trash } from "lucide-react";

interface NewList {
  allData: any;
  setAllData: any;
  addEditModalOpen: any;
}

const PracticeDocumentList = React.forwardRef((props: NewList, ref) => {
  const { show, hide } = useLoader();
  const [searchdata, setSearchQuery] = useState<any>("");
  const [pagination, setPagination] = useState(new PaginationControls());
  const [startIndex, setStartIndex] = useState(0);

  useEffect(() => {
    getDataALL(searchdata);
  }, [pagination.page, searchdata]);

  const getDataALL = async (searchdata?: any) => {
    const skip = (pagination.page - 1) * pagination.limit;
    const obj: any = { skip_number: skip };
    if (searchdata) obj.search = searchdata;

    try {
      const response = await getData(ApiUrl.listPracticeDocuments, obj, { show, hide });

      props.setAllData(response.data);
      setPagination((prev) => ({ ...prev, count: response.total_count }));
      setStartIndex(skip + 1);
    } catch (error) {
      console.error("Error fetching practice documents", error);
    }
  };

  useImperativeHandle(ref, () => ({
    getDataALL,
    handleSearchSubmit,
    handleSearchChange,
  }));

  const handlePageChange = ({ selected }: { selected: number }) => {
    setPagination((prev) => ({
      ...prev,
      page: selected + 1,
    }));
  };

  const handleSearchChange = (event: any) => {
    setSearchQuery(event);
  };

  const handleSearchSubmit = () => {
    getDataALL(searchdata);
  };

  const deleteDocument = async (doc: any) => {
    const confirm = messageService();
    confirm.confirm("Delete this practice document?", "").then(async (res) => {
      if (res.value) {
        try {
          await deleteData(ApiUrl.deletePracticeDocument + doc._id);
          getDataALL();
          confirm.toast("success", confirm.message.deleteSuccess);
        } catch (err) {
          confirm.toast("error", "Delete failed");
        }
      }
    });
  };

  return (
    <div className="p-4">
      {props.allData.length === 0 ? (
        <NoRecord noDataText="No practice documents found" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {props.allData.map((data: any) => (
            <div
              key={data._id}
              className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200 flex"
            >
              <div className="w-1/3 flex items-center justify-center bg-gray-50">
                {data.thumbnail ? (
                  <img
                    src={data.thumbnail}
                    alt="document-thumbnail"
                    className="w-full h-32 object-cover"
                  />
                ) : (
                  <FileText className="w-12 h-12 text-gray-400" />
                )}
              </div>

              <div className="w-2/3 p-4 flex flex-col justify-between">
                <div>
                  {data.isIFTA &&
                    <h3 className="text-lg font-semibold mb-2">IFTA Document</h3>
                  }
                  <h3 className="text-lg font-semibold mb-2">{data.title}</h3>
                  <b>
                    {data.type}
                  </b>



                </div>

                <div className="flex gap-2 mt-4 w-full">
                  <button
                    className="text-blue-600 hover:text-blue-800 hover:scale-110 ease-in-out duration-500"
                    onClick={() => props.addEditModalOpen(data)}
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    className="text-red-600 hover:text-red-800 hover:scale-110 ease-in-out duration-500"
                    onClick={() => deleteDocument(data)}
                    title="Delete"
                  >
                    <Trash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {props.allData.length > 0 && (
        <div className="mt-6">
          <Pagination pagination={pagination} onPageChange={handlePageChange} />
        </div>
      )}
    </div>
  );
});

export default PracticeDocumentList;
